import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, NavLink, useLocation } from 'react-router-dom';
import './header.css';
import psagHeaderLogo from '../../assets/psag-header-logo.png';
import { FaBars, FaTimes } from 'react-icons/fa';
import { useAuth } from '../../context/AuthContext';
import { useContent } from '../../context/ContentContext';
import ProfileAvatar from '../auth/ProfileAvatar';

function HeaderProfileMenu({ className = '', onNavigate }) {
  const { userEmail, userProfile, signOut, openProfileSettings } = useAuth();
  const { confirmDiscardUnsavedChanges } = useContent();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleNav = () => {
    setIsOpen(false);
    onNavigate?.();
  };

  return (
    <div className={`header-profile ${className}`.trim()} ref={menuRef}>
      <button
        type="button"
        className={`header-profile-btn ${isOpen ? 'is-open' : ''}`}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Admin profile menu"
      >
        <span className="header-profile-avatar" aria-hidden="true">
          <ProfileAvatar
            email={userEmail}
            displayName={userProfile.displayName}
            avatarUrl={userProfile.avatarUrl}
          />
        </span>
        <span className="header-profile-status" aria-hidden="true" />
      </button>

      {isOpen && (
        <div className="header-profile-menu" role="menu">
          <div className="header-profile-menu-header">
            <span className="header-profile-menu-avatar" aria-hidden="true">
              <ProfileAvatar
                email={userEmail}
                displayName={userProfile.displayName}
                avatarUrl={userProfile.avatarUrl}
              />
            </span>
            <div className="header-profile-menu-meta">
              <span className="header-profile-menu-label">Signed in</span>
              <span className="header-profile-menu-email">
                {userProfile.displayName || userEmail}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="header-profile-menu-item"
            role="menuitem"
            onClick={() => {
              handleNav();
              openProfileSettings();
            }}
          >
            Account settings
          </button>
          <Link to="/admin/activity" className="header-profile-menu-item" role="menuitem" onClick={handleNav}>
            Activity Log
          </Link>
          <button
            type="button"
            className="header-profile-menu-item header-profile-menu-item--danger"
            role="menuitem"
            onClick={() => {
              handleNav();
              if (confirmDiscardUnsavedChanges()) {
                signOut();
              }
            }}
          >
            Sign Out
          </button>
        </div>
      )}
    </div>
  );
}

function Header() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const { isAuthenticated, isLoading, userEmail, openSignIn, signOut, syncSession } = useAuth();
  const { confirmDiscardUnsavedChanges } = useContent();
  const showProfile = !isLoading && (isAuthenticated || Boolean(userEmail));

  const location = useLocation();
  const isAboutActive = ['/story', '/beliefs'].includes(location.pathname);

  useEffect(() => {
    syncSession();
  }, [location.pathname, syncSession]);

  const runLogoAnimation = useCallback(() => {
    document.querySelectorAll('.church-logo').forEach((logo) => {
      logo.classList.remove('church-logo--animate');
      // Force reflow so the browser restarts the animation.
      void logo.offsetWidth;
      logo.classList.add('church-logo--animate');
    });
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  useEffect(() => {
    let cancelled = false;
    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!cancelled) runLogoAnimation();
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
    };
  }, [location.pathname, runLogoAnimation]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleDropdown = () => {
    setDropdownVisible(!dropdownVisible);
  };

  const aboutDropdown = (isDesktop) => (
    <li
      className={`nav-item dropdown-container ${dropdownVisible ? 'active' : ''}`}
      onMouseEnter={() => isDesktop && !isMobileMenuOpen && setDropdownVisible(true)}
      onMouseLeave={() => isDesktop && !isMobileMenuOpen && setDropdownVisible(false)}
    >
      <span className={`dropdown-btn ${isAboutActive ? 'nav-current' : ''}`}>ABOUT US</span>
      {dropdownVisible && (
        <ul className="dropdown">
          <li className="dropdown-item">
            <NavLink to="/story" onClick={() => setIsMobileMenuOpen(false)}>OUR STORY</NavLink>
          </li>
          <li className="dropdown-item">
            <NavLink to="/beliefs" onClick={() => setIsMobileMenuOpen(false)}>OUR BELIEFS & VALUES</NavLink>
          </li>
        </ul>
      )}
    </li>
  );

  return (
    <header className="header">
      {/* Desktop bar: nav + logo + right actions */}
      <div className="header-desktop-bar">
        <nav className="nav desktop-nav header-nav-left" aria-label="Primary navigation left">
          <ul className="nav-list">
            <li className="nav-item">
              <NavLink to="/" end>HOME</NavLink>
            </li>
            {aboutDropdown(true)}
            <li className="nav-item">
              <NavLink to="/ministries">MINISTRIES</NavLink>
            </li>
          </ul>
        </nav>

        <div className="header-center header-center-desktop">
          <span className="header-logo-divider header-logo-divider--left" aria-hidden="true" />
          <Link to="/" className="header-logo-link" aria-label="PSAG Church home">
            <img
              src={psagHeaderLogo}
              alt="PSAG"
              className="church-logo"
            />
          </Link>
          <span className="header-logo-divider header-logo-divider--right" aria-hidden="true" />
        </div>

        <div className={`header-nav-right ${showProfile ? 'header-nav-right--with-profile' : ''}`.trim()}>
          <nav className="nav desktop-nav header-nav-right-links" aria-label="Primary navigation right">
            <ul className="nav-list">
              <li className="nav-item">
                <NavLink to="/gallery">GALLERY</NavLink>
              </li>
              <li className="nav-item">
                <NavLink to="/watch">WATCH</NavLink>
              </li>
            </ul>
          </nav>

          <div className="new-here">
            <NavLink to="/new-here" className={({ isActive }) => (isActive ? 'new-here-link active' : 'new-here-link')}>
              <button className="new-here-btn" type="button">NEW HERE</button>
            </NavLink>
          </div>

          <div className="desktop-social-icons">
            <div className="social-icons">
              <a href="https://www.facebook.com/psagchurch" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                <i className="fab fa-facebook"></i>
              </a>
              <a href="https://www.instagram.com/psagchurch/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <i className="fab fa-instagram"></i>
              </a>
              <a href="https://www.youtube.com/@psagchurch" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                <i className="fab fa-youtube"></i>
              </a>
            </div>
          </div>

          {showProfile && <HeaderProfileMenu className="header-profile--desktop" />}
        </div>
      </div>

      {/* Mobile: centered logo */}
      <div className="header-center header-center-mobile">
        <Link to="/" className="header-logo-link" aria-label="PSAG Church home">
          <img
            src={psagHeaderLogo}
            alt="PSAG"
            className="church-logo"
          />
        </Link>
      </div>

      {/* Mobile: compact right + hamburger */}
      <div className="header-mobile-actions">
        {showProfile && (
          <HeaderProfileMenu
            className="header-profile--mobile"
            onNavigate={() => setIsMobileMenuOpen(false)}
          />
        )}
        <div className="new-here new-here-mobile">
          <NavLink to="/new-here" className={({ isActive }) => (isActive ? 'new-here-link active' : 'new-here-link')}>
            <button className="new-here-btn" type="button">NEW HERE</button>
          </NavLink>
        </div>
        <button
          className="hamburger"
          onClick={toggleMobileMenu}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
        </button>
      </div>

      {createPortal(
        isMobileMenuOpen ? (
          <div className="mobile-menu-overlay" role="dialog" aria-label="Mobile menu">
            <nav className="mobile-menu-panel">
              <ul className="mobile-menu-list">
                <li className="mobile-menu-item">
                  <NavLink to="/" end onClick={() => setIsMobileMenuOpen(false)}>HOME</NavLink>
                </li>
                <li className={`mobile-menu-item dropdown-container ${dropdownVisible ? 'active' : ''}`}>
                  <span
                    className={`dropdown-btn ${isAboutActive ? 'nav-current' : ''}`}
                    onClick={toggleDropdown}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && toggleDropdown()}
                  >
                    ABOUT US
                  </span>
                  {dropdownVisible && (
                    <ul className="mobile-dropdown">
                      <li className="dropdown-item">
                        <NavLink to="/story" onClick={() => setIsMobileMenuOpen(false)}>OUR STORY</NavLink>
                      </li>
                      <li className="dropdown-item">
                        <NavLink to="/beliefs" onClick={() => setIsMobileMenuOpen(false)}>OUR BELIEFS & VALUES</NavLink>
                      </li>
                    </ul>
                  )}
                </li>
                <li className="mobile-menu-item">
                  <NavLink to="/ministries" onClick={() => setIsMobileMenuOpen(false)}>MINISTRIES</NavLink>
                </li>
                <li className="mobile-menu-item">
                  <NavLink to="/gallery" onClick={() => setIsMobileMenuOpen(false)}>GALLERY</NavLink>
                </li>
                <li className="mobile-menu-item">
                  <NavLink to="/watch" onClick={() => setIsMobileMenuOpen(false)}>WATCH</NavLink>
                </li>
                <li className="mobile-menu-item header-auth-mobile">
                  {isAuthenticated ? (
                    <>
                      <Link
                        to="/admin/activity"
                        className="mobile-activity-link"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        Activity Log
                      </Link>
                      <button type="button" className="sign-btn sign-btn--out" onClick={() => { if (confirmDiscardUnsavedChanges()) { signOut(); } setIsMobileMenuOpen(false); }}>
                        Sign Out
                      </button>
                    </>
                  ) : (
                    <button type="button" className="sign-btn" onClick={() => { openSignIn(); setIsMobileMenuOpen(false); }}>
                      Sign In
                    </button>
                  )}
                </li>
                <li className="mobile-menu-item social-icons-container">
                  <div className="social-icons">
                    <a href="https://www.facebook.com/psagchurch" target="_blank" rel="noopener noreferrer" aria-label="Facebook">
                      <i className="fab fa-facebook"></i>
                    </a>
                    <a href="https://www.instagram.com/psagchurch/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                      <i className="fab fa-instagram"></i>
                    </a>
                    <a href="https://www.youtube.com/@psagchurch" target="_blank" rel="noopener noreferrer" aria-label="YouTube">
                      <i className="fab fa-youtube"></i>
                    </a>
                  </div>
                </li>
              </ul>
            </nav>
          </div>
        ) : null,
        document.body
      )}
    </header>
  );
}

export default Header;
